/**
 * QBase 점검(쓰기 동결) 상태 — 화면 전체가 보는 한 곳.
 *
 * 알게 되는 길은 셋이다:
 *   1) QBase 직접 호출(qbaseClient) 응답의 `X-QBase-Maintenance` 헤더 · 503 MAINTENANCE · `[MAINTENANCE]` 메시지
 *   2) LiveSQL 서버(/sql-developer)가 넘겨 준 503 `resultCode:'MAINTENANCE'`
 *   3) 로그인한 동안 1분마다 `GET /qbase/api/maintenance` (로그인 없이 부르는 공개 경로)
 * 점검 중에는 `retryAfterSec`(기본 30초)마다 다시 물어 `active=false` 가 오면 배너를 내린다.
 *
 * 전환 마지막의 QBase 재기동(15~30초 접속 거부)은 **마지막 단계가 SWITCHING 이면** 장애가 아니라
 * 「DB 전환 중」 으로 본다(restarting). 저장을 자동으로 다시 보내지 않는다 — 두 번 저장될 수 있다.
 */
import { QBASE_API } from '../config/qbase';
import { IS_STANDALONE } from '../config/features';

const STATUS_PATH = '/qbase/api/maintenance';
const HEADER = 'X-QBase-Maintenance';
const DEFAULT_RETRY_SEC = 30;
const SWITCH_RETRY_SEC = 10;
const IDLE_POLL_MS = 60 * 1000;

let state = { active: false };
const listeners = new Set();
let timer = null;
let idleTimer = null;
let inflight = null;

function emit() {
  for (const fn of listeners) {
    try { fn(state); } catch (_e) { /* listener error must not break others */ }
  }
}

function normalize(m, phase) {
  const o = m && typeof m === 'object' ? m : {};
  return {
    active: true,
    readOnly: o.readOnly !== false,
    phase: o.phase || phase || state.phase || 'WRITE_FREEZE',
    reason: o.reason || null,
    message: o.message || null,
    since: o.since || null,
    expiresAt: o.expiresAt || null,
    retryAfterSec: Number(o.retryAfterSec) > 0 ? Number(o.retryAfterSec) : DEFAULT_RETRY_SEC,
    restarting: !!o.restarting,
  };
}

function schedule() {
  clearTimeout(timer);
  timer = null;
  if (!state.active) return;
  const sec = state.restarting ? SWITCH_RETRY_SEC : (state.retryAfterSec || DEFAULT_RETRY_SEC);
  timer = setTimeout(() => { refreshMaintenance(); }, sec * 1000);
}

function set(next) {
  const prevActive = state.active;
  state = next;
  if (state.active || prevActive) emit();
  schedule();
}

export function getMaintenance() {
  return state;
}

export function subscribeMaintenance(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** 점검을 알게 됐다(503 본문 · 헤더) — 상태를 켜고 주기 확인을 시작한다 */
export function noteMaintenance(m, phase) {
  if (IS_STANDALONE) return;
  const wasActive = state.active;
  // 헤더만 본 경우 사유·해제 시각을 모르므로 이미 아는 값을 덮지 않는다
  const merged = m ? normalize({ ...(wasActive ? state : {}), ...m, restarting: !!m.restarting }, phase) : normalize(wasActive ? state : null, phase);
  set(merged);
  if (!m && !wasActive) refreshMaintenance();
}

/**
 * 응답이 「점검이라 막혔다」 인지 — 맞으면 maintenance 객체(상태도 켠다), 아니면 null.
 * 헤더만 있고 성공한 응답(동결 중 조회)은 상태만 켜고 null 을 돌려준다.
 */
export function detectMaintenance(status, body, headerPhase) {
  if (IS_STANDALONE) return null;
  const msg = body && typeof body === 'object'
    ? (body.resultMessage || body.message || body.error || body.resultMsg || '')
    : '';
  let m = null;
  if (body && body.resultCode === 'MAINTENANCE') m = normalize(body.maintenance, headerPhase);
  else if (status >= 400 && typeof msg === 'string' && msg.startsWith('[MAINTENANCE]')) {
    m = normalize({ message: msg.replace(/^\[MAINTENANCE\]\s*/, '') || null }, headerPhase);
  }
  if (m) noteMaintenance(m, headerPhase);
  else if (headerPhase) noteMaintenance(null, headerPhase);
  return m;
}

/** fetch Response 의 헤더에서 단계를 읽는다 */
export function headerPhaseOf(res) {
  try { return (res && res.headers && res.headers.get(HEADER)) || null; } catch (_e) { return null; }
}

/** QBase 에 지금 상태를 묻는다. 닿지 않으면 마지막 단계로 판정한다(SWITCHING → 재기동 중) */
export function refreshMaintenance() {
  if (IS_STANDALONE) return Promise.resolve(state);
  if (inflight) return inflight;
  inflight = (async () => {
    let res;
    try {
      res = await fetch(`${QBASE_API}${STATUS_PATH}`, { credentials: 'include', headers: { Accept: 'application/json' }, cache: 'no-store' });
    } catch (_e) {
      unreachable();
      return state;
    }
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      const body = await res.json().catch(() => null);
      if (body && body.maintenance) set(normalize(body.maintenance, headerPhaseOf(res)));
      else unreachable();
      return state;
    }
    const body = await res.json().catch(() => null);
    const m = body && body.maintenance;
    if (res.ok && m && m.active) set(normalize(m, headerPhaseOf(res)));
    else if (res.ok && m) set({ active: false });
    else schedule();   // unexpected answer: keep what we know and try again later
    return state;
  })().finally(() => { inflight = null; });
  return inflight;
}

function unreachable() {
  if (state.active && state.phase === 'SWITCHING') set({ ...state, restarting: true });
  else schedule();   // not in maintenance as far as we know: QBase is just down, banner stays as it was
}

/** 로그인한 동안 1분마다 가볍게 확인한다(점검이 시작된 것을 저장을 누르기 전에 알리려고) */
export function startMaintenanceWatch() {
  if (IS_STANDALONE || idleTimer) return () => {};
  const tick = () => {
    if (!state.active && !(typeof document !== 'undefined' && document.hidden)) refreshMaintenance();
  };
  tick();
  idleTimer = setInterval(tick, IDLE_POLL_MS);
  return () => { clearInterval(idleTimer); idleTimer = null; };
}

/** 저장이 점검 때문에 막혔는지 — 오류 객체(QbaseError · axios 오류 · fetch 본문)를 가리지 않는다 */
export function isMaintenanceError(err) {
  if (!err) return false;
  if (err.code === 'MAINTENANCE') return true;
  const data = (err.response && err.response.data) || err.body;
  return !!(data && (data.resultCode === 'MAINTENANCE' || data.errorType === 'QBASE_MAINTENANCE'));
}
